import { useState } from "react";
import { CheckCircle2, Loader2, ShieldCheck, XCircle } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { fmtDateTime } from "@/lib/intl";
import { Button } from "@/components/ui/button";

const Tick = ({ ok, label, testid }) => (
  <span className={`inline-flex items-center gap-1 ${ok ? "text-emerald-700" : "text-rose-700"}`} data-testid={testid}>
    {ok ? <CheckCircle2 className="h-3.5 w-3.5" /> : <XCircle className="h-3.5 w-3.5" />}{label}
  </span>
);

const PageResult = ({ p }) => (
  <div className="space-y-1 rounded border border-zinc-200 p-2" data-testid={`meta-leads-check-${p.page_id}`}>
    <div className="font-medium text-zinc-900">{p.page_name}</div>
    <div className="flex flex-wrap gap-x-3 gap-y-1">
      <Tick ok={p.token_valid} label="Page access valid" testid={`meta-leads-check-token-${p.page_id}`} />
      <Tick ok={p.leads_permission} label="leads_retrieval granted" testid={`meta-leads-check-perm-${p.page_id}`} />
      <Tick ok={p.leadgen_subscribed} label="leadgen webhook on" testid={`meta-leads-check-sub-${p.page_id}`} />
      <Tick ok={p.forms != null && !p.error} label={p.forms != null ? `${p.forms} lead form(s) readable` : "lead forms readable"}
        testid={`meta-leads-check-forms-${p.page_id}`} />
    </div>
    {p.latest_lead_at && <div className="text-zinc-600">Newest lead Facebook has: {fmtDateTime(p.latest_lead_at)} — leads can be read.</div>}
    {p.error && <div className="break-words text-rose-700" data-testid={`meta-leads-check-error-${p.page_id}`}>{p.error}</div>}
  </div>
);

/** Live check that each connected Page can deliver and be read for lead forms (Leads Access, permission, webhook). */
export const LeadAccessCheck = ({ disabled, onChecked }) => {
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const r = await api.post("/meta/leads/check", null, { timeout: 60000 });
      setRes(r.data.pages);
      onChecked?.();
    } catch (e) {
      toast.error("Couldn't check lead access", { description: e?.response?.data?.detail || e.message });
    } finally { setBusy(false); }
  };
  return (
    <div className="space-y-1.5">
      <Button size="sm" variant="outline" className="h-7 text-xs" onClick={run} disabled={busy || disabled} data-testid="meta-leads-check-btn">
        {busy ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <ShieldCheck className="mr-1 h-3 w-3" />}Check lead access
      </Button>
      {res && (res.length ? res.map((p) => <PageResult key={p.page_id} p={p} />)
        : <div className="text-zinc-500" data-testid="meta-leads-check-none">No Pages connected.</div>)}
    </div>
  );
};
